import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Loader from "../../../common/Loader";
import ImageComponent from '../../../../components/common/Image';
import { dealerSelfie, setStage, setSubStage } from '../../../../slices/eligibility/eligibilitySlice';
import { Padding } from '@mui/icons-material';

function DealerSelfie() {
  const dispatch = useDispatch();
  const { stage, subStage } = useSelector((state) => state.eligibility);
  const { referenceId } = useSelector((state) => state.dashboard);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [loader, setLoader] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [custAmount, setCustAmount] = useState(false);
    const [geoErrorPopup, setGeoErrorPopup] = useState(false)

  // Add this useEffect to monitor stage changes
  useEffect(() => {
  }, [stage, subStage]);

  // Auto-start camera on component mount
  useEffect(() => {
    startCamera();
  }, []);

  const startCamera = async () => {
    setError(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        }
      });
      streamRef.current = mediaStream;
      setStream(mediaStream);

      // Use a more reliable method to assign stream
      const assignStream = () => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;

          videoRef.current.onloadedmetadata = () => {
          };

          videoRef.current.oncanplay = () => {
          };
        } else {
          // Retry after a longer delay if ref is not ready
          setTimeout(assignStream, 200);
        }
      };

      setTimeout(assignStream, 100);

    } catch (error) {
      console.error('Error accessing camera:', error);
      setError(`Camera error: ${error.message}`);
    }
  };

  const stopCamera = () => {
    const currentStream = streamRef.current || stream;
    if (currentStream) {
      try {
        currentStream.getTracks().forEach(track => {
          if (track && typeof track.stop === 'function') {
            track.stop();
          }
        });
      } catch (error) {
        console.error('Error stopping camera tracks:', error);
      }
      streamRef.current = null;
      setStream(null);
    }
  };

  const captureSelfie = () => {
    if (videoRef.current && canvasRef.current && videoRef.current.videoWidth > 0) {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);

        const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        const base64Image = imageDataUrl.split(',')[1];

        setCapturedImage({
          dataUrl: imageDataUrl,
          base64: base64Image
        });

        stopCamera();
      } else {
        console.error('Unable to get canvas context');
        setError('Failed to capture image');
      }
    } else {
      console.error('Video or canvas not ready for capture');
      setError('Camera not ready, please try again');
    }
  };

  const renderFooterButtons = () => {
    if (stream && !capturedImage) {
      return (
        <button className="btn btn-primary" type="button" onClick={captureSelfie}>
          Capture
        </button>
      );
    }

    if (capturedImage) {
      return (
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleProceed}
          disabled={buttonLoading}
        >
          {buttonLoading ? 'Processing...' : 'Proceed'}
        </button>
      );
    }

    return (
      <button className="btn btn-primary" type="button" disabled>
        Capture
      </button>
    );
  };

  const retakeSelfie = () => {
    setCapturedImage(null);
    startCamera();
  };

 const getUserLocation = async (shouldUseGeolocation = true) => {
  if (!shouldUseGeolocation) {
    throw new Error("Geolocation access is not necessary.");
  }

  if (!navigator.geolocation) {
    toast.error("Geolocation is not supported by your browser");
    throw new Error("Geolocation is not supported by your browser");
  }

  return new Promise((resolve, reject) => {
    // NOSONAR
    navigator.geolocation.getCurrentPosition( // NOSONAR
      (position) => {
        resolve(position.coords);
      },
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            toast.error("Please allow location access to continue");
            break;

          case err.POSITION_UNAVAILABLE:
            toast.error("Location information unavailable");
            break;

          case err.TIMEOUT:
            toast.error("Location request timed out");
            break;

          default:
            toast.error("Error getting location");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

  const handleProceed = async () => {
    if (!capturedImage) {
      alert('Please capture a selfie first');
      return;
    }
    const location = await getUserLocation();

    setButtonLoading(true);
    setLoader(true);

    const apiData = {
      reference_id: referenceId,
      dealer_image: capturedImage.base64,
      latitude: "" + location.latitude,
      longitude: "" + location.longitude,
    };

    try {

      const response = await dispatch(dealerSelfie(apiData)).unwrap();

      if (response?.response_code === 'S100') {
        setCustAmount(response?.response_string)

      }
      else if (response?.response_code === 'E154') {
          setGeoErrorPopup(response?.response_string)
        }
         else {
        !response?.payload?.error &&
          toast.error(response?.response_string || 'Something went wrong');
      }
    } catch (error) {
      console.error('API call failed:', error);
    } finally {
      setLoader(false);
      setButtonLoading(false);
    }
  };

  // Fixed cleanup useEffect - ensure proper cleanup function
  useEffect(() => {
    // Cleanup function
    const cleanup = () => {
      const currentStream = streamRef.current;
      if (currentStream && currentStream.getTracks) {
        try {
          currentStream.getTracks().forEach(track => {
            if (track && typeof track.stop === 'function') {
              track.stop();
            }
          });
          streamRef.current = null;
        } catch (error) {
          console.error('Error in cleanup:', error);
        }
      }
    };

    // Return the cleanup function
    return cleanup;
  }, []); // Empty dependency array

  return (
    <>
      <div className="container-fluid ">
        {loader === true && <Loader />}
        <div className="card">
          <div className="dealer-body">
            <div className="row">
              <div className="col-12">
                <div className="selfie-create text-center align-content-center">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={!stream && !capturedImage ? startCamera : undefined}
                    onKeyDown={(e) => {
                      if (!stream && !capturedImage && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        startCamera();
                      }
                    }}
                    style={{ cursor: !stream && !capturedImage ? 'pointer' : 'default' }}
                  >
                    {!stream && !capturedImage && (
                      <>
                        <ImageComponent className="img-fluid" fileName="selfie.svg" alt="selfie" />
                        <p className=" mb-3 fs-6 dark-blue">Take a Selfie</p>
                      </>
                    )}
                  </div>

                  {stream && !capturedImage && (
                    <div>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{
                          width: '100%',
                          maxWidth: '197px',
                          height: '197px',
                          borderRadius: '50%',
                          backgroundColor: '#000'
                        }}
                      />
                      <canvas ref={canvasRef} style={{ display: 'none' }} />
                      {error && <p style={{ color: 'red' }}>{error}</p>}
                    </div>
                  )}

                  {capturedImage && (
                    <div>
                      {capturedImage && (
                        <div>
                          <img loading="lazy"
                            src={capturedImage.dataUrl}
                            alt="Captured selfie"
                            className="img-fluid"
                            style={{
                              width: '197px',
                              maxWidth: '197px',
                              height: '197px',
                              borderRadius: '50%',
                            }}
                          />
                          <div className="mt-2 mb-2">
                            <div className="btn btn-gender-retake btn-outline-primary" htmlFor="option2">
                              <span className="icon-mediacamera mx-1"></span>
                              <button className="bg-transparent border-0 p-0 text-decoration-underline" type="button" onClick={retakeSelfie} disabled={buttonLoading}>Retake</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!capturedImage && (
                    <div className="d-flex justify-content-center gap-2 align-items-center">
                      <div className="address-box fs-8 fw-normal position-relative text-center selfie-rules-box ">
                        <div className="selfie-c">
                          No caps glasses
                          spectacles
                        </div>
                        <div className="selfie-rules">
                          <ImageComponent className="img-fluid" fileName="wrongimg.svg" alt="wrongimg" />
                        </div>
                      </div>
                      <div className="address-box fs-8 fw-normal position-relative text-center selfie-rules-box">
                        <div className="selfie-c">
                          No Blurry Image
                        </div>
                        <div className="selfie-rules">
                          <ImageComponent className="img-fluid" fileName="wrongimg.svg" alt="wrongimg" />
                        </div>
                      </div>
                      <div className="address-box fs-8 fw-normal position-relative text-center selfie-rules-box">
                        <div className="selfie-c">
                          Bright Light
                        </div>
                        <div className="selfie-rules ">
                          <ImageComponent className="img-fluid" fileName="tickimage.svg" alt="tickimage" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {custAmount &&
        <>
          <div className="modal fade show p-0" id="SelectRole" aria-labelledby="SelectRole" tabIndex="-1" style={{ display: "block", Padding: "0px" }} aria-modal="true">
            <div
              className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg bottom-modal"
            >
              <div className="modal-content">
                <div className="modal-body text-center mt-3 mb-3">
                  <div className="row gy-3">
                    <div className="col-lg-12 col-md-12 col-sm-12 ">
                      <ImageComponent fileName="new-congratulations.gif" className="img-fluid gif-tick" alt="ico-success" />
                      <p className="small fs-6">{custAmount}</p>
                    </div>
                    <div className="col-lg-12 col-md-12 col-sm-12 ">
                      <button className="btn btn-primary w-50" type="button" onClick={() => {
                        dispatch(setStage("product"));
                        dispatch(setSubStage("brand"));
                      }}>Proceed</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      }
      {
              geoErrorPopup &&
              <>
                <div className="modal show fade p-0" id="geoerrorpopup" tabIndex="-1" style={{ display: "block", Padding: "0px" }} aria-labelledby="geoerrorpopup" aria-hidden="true">
                  <div className="modal-dialog bottom-modal">
                    <div className="modal-content">
                      <div className="px-3 py-3 text-center">
                        <ImageComponent fileName="geo-error.svg" />
                        <p className="text-danger fw-600 fs-32 mt-4">ERROR !</p>
                        <p className="mt-3">{geoErrorPopup}</p>
                        <div className="d-flex align-items-center justify-content-center mt-4">
                          <div className='w-75'>
                            <button type="button" className="btn btn-primary w-100" onClick={() => setGeoErrorPopup(false)}>Proceed</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-backdrop fade show"></div>
              </>
            }

      <footer className="sticky-bottom-1">
        <div className="container-fluid ">
          <div className="row">
            <div className="col-sm-12 ">
              <div className="d-flex justify-content-end">
                {renderFooterButtons()}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default DealerSelfie;